import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Calendar from 'components/Calendar';
import DropdownSelect from 'components/inputs/DropdownSelect/DropdownSelect';
import { AvailableLocales } from 'helpers/dateHelpers';

import { PeriodCaption, PeriodSelectorWrapper } from './Styled';

export type PeriodType =
  | 'ALL'
  | 'LAST_12_MONTHS'
  | 'CURRENT_YEAR'
  | 'PREVIOUS_YEAR'
  | 'CUSTOM';

export interface ReportPeriodValue {
  type: PeriodType;
  startDate: string | null; // YYYY-MM-DD
  finishDate: string | null; // YYYY-MM-DD
}

export const ALL_TIME_PERIOD: ReportPeriodValue = {
  type: 'ALL',
  startDate: null,
  finishDate: null
};

const API_DATE_FORMAT = 'YYYY-MM-DD';
const CAPTION_DATE_FORMAT = 'DD/MM/YYYY';
const CALENDAR_LOCALES: AvailableLocales[] = ['es', 'en', 'pt'];

const PERIOD_LABELS: Record<PeriodType, string> = {
  ALL: 'report_page:period_all',
  LAST_12_MONTHS: 'report_page:period_last_12_months',
  CURRENT_YEAR: 'report_page:period_current_year',
  PREVIOUS_YEAR: 'report_page:period_previous_year',
  CUSTOM: 'report_page:period_custom'
};

// Las fechas se arman con la fecha local del navegador y viajan como
// YYYY-MM-DD; nunca con toISOString, que puede correr el día.
export const getPresetPeriod = (
  type: PeriodType,
  today = moment()
): ReportPeriodValue => {
  const format = (date: moment.Moment) => date.format(API_DATE_FORMAT);
  switch (type) {
    case 'LAST_12_MONTHS':
      return {
        type,
        startDate: format(today.clone().subtract(1, 'year').add(1, 'day')),
        finishDate: format(today)
      };
    case 'CURRENT_YEAR':
      return {
        type,
        startDate: format(today.clone().startOf('year')),
        finishDate: format(today)
      };
    case 'PREVIOUS_YEAR': {
      const previousYear = today.clone().subtract(1, 'year');
      return {
        type,
        startDate: format(previousYear.clone().startOf('year')),
        finishDate: format(previousYear.clone().endOf('year'))
      };
    }
    case 'CUSTOM':
      return { type, startDate: null, finishDate: null };
    default:
      return ALL_TIME_PERIOD;
  }
};

export const isPeriodComplete = (period: ReportPeriodValue) =>
  period.type === 'ALL' || (!!period.startDate && !!period.finishDate);

interface PeriodSelectorProps {
  value: ReportPeriodValue;
  onChange: (value: ReportPeriodValue) => void;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({ value, onChange }) => {
  const { t, i18n } = useTranslation(['report_page']);

  const options = (Object.keys(PERIOD_LABELS) as PeriodType[]).map(type => ({
    label: t(PERIOD_LABELS[type]),
    value: type
  }));
  const selectedOption = options.find(option => option.value === value.type);

  const calendarLocale = CALENDAR_LOCALES.includes(
    i18n.language as AvailableLocales
  )
    ? (i18n.language as AvailableLocales)
    : 'es';

  const onSelectCustomRange = (startDate: Date, endDate: Date) => {
    onChange({
      type: 'CUSTOM',
      startDate: moment(startDate).format(API_DATE_FORMAT),
      finishDate: moment(endDate).format(API_DATE_FORMAT)
    });
  };

  return (
    <PeriodSelectorWrapper>
      <DropdownSelect
        onSelect={option => onChange(getPresetPeriod(option.value))}
        value={selectedOption}
        defaultValue={selectedOption}
        options={options}
      />
      {value.type === 'CUSTOM' && (
        <Calendar locale={calendarLocale} onChange={onSelectCustomRange} />
      )}
      {value.startDate && value.finishDate && (
        <PeriodCaption>
          {`${moment(value.startDate, API_DATE_FORMAT).format(
            CAPTION_DATE_FORMAT
          )} – ${moment(value.finishDate, API_DATE_FORMAT).format(
            CAPTION_DATE_FORMAT
          )}`}
        </PeriodCaption>
      )}
    </PeriodSelectorWrapper>
  );
};

export default PeriodSelector;
